import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderRef, setOrderRef] = useState("");

  const [orderData, setOrderData] = useState(() => {
    try {
      const saved = localStorage.getItem("yono_order");
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const data = orderData || { product: "HPA T3", selectedOptions: [], quantity: 1 };

  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    customer_address: "", customer_city: "", customer_postal_code: "", notes: "",
  });

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submitOrder = async () => {
    if (!form.customer_name || !form.customer_email) {
      toast.error("Veuillez remplir les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        product: data.product,
        options: data.selectedOptions.map((o) => ({ name: o.name })),
        quantity: data.quantity,
        ...form,
      });
      setOrderRef(res.data.reference);
      localStorage.removeItem("yono_order");
      setStep(3);
    } catch {
      toast.error("Erreur lors de l'envoi de la demande. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="order-page" className="pt-20 min-h-screen">
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto">
          {/* Progress */}
          <div className="flex items-center gap-4 mb-16">
            {["Récapitulatif", "Coordonnées", "Confirmation"].map((label, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center border text-xs font-mono ${
                  step > i + 1 ? "bg-soft-black text-off-white border-soft-black" :
                  (step === i + 1 ? "border-soft-black text-soft-black" : "border-warm-grey/50 text-warm-grey")
                }`}>
                  {step > i + 1 ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span className={`hidden sm:block font-mono text-xs uppercase tracking-wider ${step === i + 1 ? "text-soft-black" : "text-warm-grey"}`}>
                  {label}
                </span>
                {i < 2 && <div className="w-8 h-px bg-warm-grey/50" />}
              </div>
            ))}
          </div>

          {/* Step 1: Recap */}
          {step === 1 && (
            <div data-testid="order-step-1">
              <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Votre demande de devis</span>
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mt-4 mb-8">Récapitulatif</h1>

              <div className="border border-warm-grey/30 divide-y divide-warm-grey/30">
                <div className="p-6 flex justify-between items-center">
                  <div>
                    <h3 className="font-heading text-lg">{data.product}</h3>
                    <p className="font-body text-sm text-soft-black/60">Habitat bois 4 couchages - 13 m2</p>
                  </div>
                  <span data-testid="order-price-on-request" className="font-mono text-sm">Prix sur demande</span>
                </div>
                {data.selectedOptions.length > 0 && data.selectedOptions.map((opt, i) => (
                  <div key={i} className="p-6 flex justify-between">
                    <span className="font-body text-sm">{opt.name}</span>
                    <span className="font-mono text-sm">Sur devis</span>
                  </div>
                ))}
                <div className="p-6 bg-light-beige">
                  <p className="font-body text-sm text-soft-black/60">Un devis personnalisé vous sera transmis selon votre configuration et votre terrain.</p>
                </div>
              </div>

              <div className="flex justify-end mt-8">
                <button data-testid="order-next-step-1" onClick={() => setStep(2)}
                  className="flex items-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300">
                  Continuer <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Coordonnées */}
          {step === 2 && (
            <div data-testid="order-step-2">
              <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Vos informations</span>
              <h2 className="font-heading text-3xl tracking-tight text-soft-black mt-4 mb-8">Coordonnées</h2>

              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">Nom complet *</Label>
                    <Input name="customer_name" data-testid="order-name" value={form.customer_name} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" placeholder="Nom et prenom" />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">Email *</Label>
                    <Input name="customer_email" type="email" data-testid="order-email" value={form.customer_email} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" placeholder="votre@email.com" />
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">Téléphone</Label>
                  <Input name="customer_phone" data-testid="order-phone" value={form.customer_phone} onChange={handleChange}
                    className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" placeholder="+33 6 00 00 00 00" />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">Adresse</Label>
                  <Input name="customer_address" data-testid="order-address" value={form.customer_address} onChange={handleChange}
                    className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" placeholder="Rue, numéro" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">Ville</Label>
                    <Input name="customer_city" data-testid="order-city" value={form.customer_city} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" placeholder="Ville" />
                  </div>
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">Code postal</Label>
                    <Input name="customer_postal_code" data-testid="order-postal" value={form.customer_postal_code} onChange={handleChange}
                      className="mt-2 border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-soft-black font-body" placeholder="00000" />
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/60">Notes</Label>
                  <textarea name="notes" data-testid="order-notes" value={form.notes} onChange={handleChange} rows={3}
                    className="mt-2 w-full border-0 border-b border-soft-black/20 rounded-none bg-transparent px-0 py-3 font-body text-sm resize-none focus-visible:outline-none focus-visible:border-soft-black"
                    placeholder="Informations complémentaires..." />
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button data-testid="order-back-step-2" onClick={() => setStep(1)}
                  className="flex items-center gap-2 border border-soft-black text-soft-black px-6 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-soft-black hover:text-off-white transition-colors duration-300">
                  <ArrowLeft className="h-3.5 w-3.5" /> Retour
                </button>
                <button data-testid="order-submit" onClick={submitOrder} disabled={loading}
                  className="flex items-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300 disabled:opacity-50">
                  {loading ? "Envoi..." : "Envoyer ma demande de devis"} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div data-testid="order-step-3" className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-8 border-2 border-natural-wood flex items-center justify-center">
                <Check className="h-8 w-8 text-natural-wood" />
              </div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-natural-wood">Demande envoyée</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mt-4 mb-4">Merci pour votre demande</h2>
              <p className="font-body text-soft-black/60 mb-2">Votre référence de demande :</p>
              <span className="font-mono text-lg text-soft-black bg-light-beige px-6 py-3 inline-block" data-testid="order-reference">{orderRef}</span>
              <p className="font-body text-sm text-soft-black/50 mt-8 max-w-md mx-auto">
                Notre équipe vous contactera sous 48h pour vous transmettre un devis personnalisé.
              </p>
              <button data-testid="order-back-home" onClick={() => navigate("/")}
                className="mt-8 inline-flex items-center gap-2 border border-soft-black text-soft-black px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-soft-black hover:text-off-white transition-colors duration-300">
                Retour à l'accueil
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
